/**
 * REVISÃO AUTOMÁTICA DE DESIGN (sem custo de IA)
 * Aplica as regras que já validamos na prática: contraste WCAG, legibilidade
 * do botão, estrelas douradas e QR sempre escaneável.
 * Foi exatamente esse tipo de erro (título escuro sobre faixa verde) que
 * passou despercebido — agora o próprio sistema acha e corrige.
 */
import { mix } from './tema';

/** Luminância relativa WCAG (com correção gama) — a conta certa, não a simplificada. */
function relLum(hex) {
  const h = String(hex).replace('#', '');
  if (h.length !== 6) return null;
  const [r, g, b] = [0, 2, 4].map(i => {
    let v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razão de contraste entre duas cores (1 a 21). AA pede 4.5 para texto normal. */
export function contraste(a, b) {
  const L1 = relLum(a), L2 = relLum(b);
  if (L1 == null || L2 == null) return null;
  const hi = Math.max(L1, L2), lo = Math.min(L1, L2);
  return +((hi + 0.05) / (lo + 0.05)).toFixed(2);
}

const ehHex = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v.trim());

/** Empurra `cor` para preto ou branco até atingir o contraste mínimo sobre `fundo`. */
function corrigirPara(cor, fundo, minimo = 4.5) {
  if (!ehHex(cor) || !ehHex(fundo)) return null;
  const alvo = relLum(fundo) > 0.5 ? '#000000' : '#ffffff';
  for (let t = 0.1; t <= 1; t += 0.06) {
    const nova = mix(cor, alvo, t);
    if (contraste(nova, fundo) >= minimo) return nova;
  }
  return alvo;
}

/**
 * Analisa um tema e devolve { problemas[], correcoes{} }.
 * `correcoes` já vem pronto para aplicar por cima das cores do cliente.
 */
export function revisarTema(t = {}) {
  const problemas = [];
  const correcoes = {};
  const fundo = ehHex(t.bg) ? t.bg : null;      // gradiente não dá para medir com segurança
  const AA = 4.5, AA_GRANDE = 3;

  const checar = (corKey, corValor, contra, minimo, rotulo) => {
    if (!ehHex(corValor) || !ehHex(contra)) return;
    const c = contraste(corValor, contra);
    if (c != null && c < minimo) {
      const nova = corrigirPara(corValor, contra, minimo);
      problemas.push({ campo: corKey, rotulo, contraste: c, minimo, corrigidoPara: nova });
      if (nova) correcoes[corKey] = nova;
    }
  };

  if (fundo) {
    // `ink` é o texto base (título E textos menores) — exige o mínimo de texto normal
    checar('ink', t.ink, fundo, AA, 'Texto principal sobre o fundo');
    checar('sub', t.sub, fundo, AA, 'Texto secundário sobre o fundo');
    checar('accent2', t.accent2, fundo, AA, 'Categoria/selo sobre o fundo');
  }

  // texto do botão precisa ser legível sobre a cor do botão
  if (ehHex(t.accent)) {
    const tinta = ehHex(t.onAccent) ? t.onAccent : '#ffffff';
    const c = contraste(tinta, t.accent);
    if (c != null && c < AA) {
      const nova = relLum(t.accent) > 0.5 ? '#141518' : '#ffffff';
      problemas.push({ campo: 'onAccent', rotulo: 'Texto do botão principal', contraste: c, minimo: AA, corrigidoPara: nova });
      correcoes.onAccent = nova;
    }
  }

  // cabeçalho sobre faixa colorida (o erro do Verde Natural)
  if (t.topoCurvo && ehHex(t.inkTopo)) {
    const faixa = ehHex(t.topoCurvo) ? t.topoCurvo : (String(t.topoCurvo).match(/#[0-9a-f]{6}/i) || [])[0];
    if (faixa) checar('inkTopo', t.inkTopo, faixa, AA_GRANDE, 'Nome do negócio sobre a faixa colorida');
  }

  // estrelas: fora do dourado o usuário não reconhece como avaliação do Google
  if (ehHex(t.star) && t.star.toLowerCase() !== '#fbbc04') {
    problemas.push({ campo: 'star', rotulo: 'Estrelas fora do padrão Google', contraste: null, minimo: null, corrigidoPara: '#FBBC04' });
    correcoes.star = '#FBBC04';
  }

  return { problemas, correcoes, ok: problemas.length === 0 };
}

/** Texto amigável para mostrar ao usuário. */
export function resumoRevisao({ problemas }) {
  if (!problemas.length) return 'Tudo certo! Nenhum problema de legibilidade encontrado.';
  const n = problemas.length;
  return `${n} ajuste${n > 1 ? 's' : ''} de legibilidade aplicado${n > 1 ? 's' : ''}: ` +
    problemas.map(p => p.rotulo.toLowerCase()).join(', ') + '.';
}
